import { navigationLayout, navigationGroups, sectionForPath } from '@/utils/navigationLayout';
import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Calendar,
  Users,
  FileText,
  DollarSign,
  BarChart3,
  Settings,
  ChevronDown,
  LogOut,
  MapPin,
  Menu,
  X,
  ClipboardCheck,
  UserCog,
  MessageSquare,
  Briefcase,
  Building2, Handshake } from 'lucide-react';
import { logout } from '@/services/auth';
import { getCosignInbox } from '@/services/clinical';
import { getOpenClaimActionRequestCount } from '@/services/claimActionRequests';
import { getMessagesUnreadCount } from '@/services/messages';
import useAuthStore from '@/stores/auth';
import { tenantHasFeature } from '@/utils/planFeatures';

// roles omitted = visible to everyone. Role-filtered 2026-08-18: a CA
// staring at Billing/PI/Reporting entries that 403 is pure noise — the
// nav now shows each role only what they act on. Owner sees everything.
const navItems = [
  // Home = the owner's pulse/metrics view. For staff roles /dashboard just
  // redirects to For You, so showing both was duplicate clutter — exactly
  // what For You exists to eliminate (Daniel 2026-08-18). Same reason
  // Today left the nav: its queues are all For You boxes now, and the
  // page survives only as a drill-in target from those boxes.
  { to: '/dashboard', group: 'Practice', label: 'Home', icon: LayoutDashboard, roles: ['owner'] },
  { to: '/for-you', group: 'Daily work', label: 'For You', icon: ClipboardCheck, roles: ['doctor', 'ca', 'billing_staff', 'office_manager', 'owner'] },
  { to: '/schedule', group: 'Daily work', label: 'Schedule', icon: Calendar },
  { to: '/patients', group: 'Daily work', label: 'Patients', icon: Users },
  { to: '/facilities', group: 'Clinical', label: 'Facilities', icon: Building2, roles: ['doctor', 'office_manager', 'owner'] },
  { to: '/clinical', group: 'Clinical', label: 'Clinical Notes', icon: FileText, roles: ['doctor', 'office_manager', 'owner'] },
  { to: '/messages', group: 'Daily work', label: 'Messages', icon: MessageSquare },
  { to: '/billing', group: 'Billing & PI', label: 'Billing', icon: DollarSign, roles: ['billing_staff', 'office_manager', 'owner'] },
  { to: '/pi', group: 'Billing & PI', label: 'PI Hub', icon: Briefcase, roles: ['doctor', 'ca', 'billing_staff', 'office_manager', 'owner'], feature: 'pi' },
  { to: '/front-desk-tasks', group: 'Daily work', label: 'Front Desk Tasks', icon: UserCog, roles: ['ca', 'office_manager', 'owner'] },
  { to: '/referrals', group: 'Daily work', label: 'Referrals', icon: Handshake, roles: ['doctor', 'ca', 'billing_staff', 'office_manager', 'owner'], feature: 'referral_partners' },
  { to: '/reports', group: 'Practice', label: 'Reports', icon: BarChart3, roles: ['office_manager', 'owner'] },
  { to: '/my-stats', group: 'Practice', label: 'My Stats', icon: BarChart3, providersOnly: true },
  { to: '/settings', group: 'Practice', label: 'Settings', icon: Settings },
];

export default function TopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const drawerRef = useRef(null);
  const openerRef = useRef(null);
  const moreRef = useRef(null);
  const [compact, setCompact] = useState(() => !window.matchMedia('(min-width: 1280px)').matches);
  useEffect(() => {
    const media=window.matchMedia('(min-width: 1280px)');
    const update=()=>{setCompact(!media.matches);if(moreRef.current)moreRef.current.open=false;};
    const desktop = window.matchMedia('(min-width: 768px)');
    const dismissMore = () => { if (moreRef.current) moreRef.current.open = false; };
    media.addEventListener('change',update);
    desktop.addEventListener('change',dismissMore);
    return () => {
      media.removeEventListener('change',update);
      desktop.removeEventListener('change',dismissMore);
    };
  }, []);
  useEffect(()=>{
    const dismiss=event=>{if(moreRef.current&&!moreRef.current.contains(event.target))moreRef.current.open=false;};
    const escape=event=>{if(event.key==='Escape'&&moreRef.current?.open){moreRef.current.open=false;moreRef.current.querySelector('summary')?.focus();}};
    document.addEventListener('pointerdown',dismiss);document.addEventListener('focusin',dismiss);document.addEventListener('keydown',escape);
    return()=>{document.removeEventListener('pointerdown',dismiss);document.removeEventListener('focusin',dismiss);document.removeEventListener('keydown',escape);};
  }, []);
  const { user, tenant, locations, currentLocationId, setCurrentLocation, clearAuth, refreshToken } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  // providersOnly nav items (My Stats) hide for non-provider staff
  const isProviderUser = user?.is_provider || user?.role === 'doctor' || user?.role === 'owner' || user?.role === 'office_manager';
  const visibleNavItems = navItems.filter((i) => (!i.providersOnly || isProviderUser)
    && (!i.roles || !user?.role || i.roles.includes(user.role))
    && tenantHasFeature(tenant, i.feature));

  const {primary,secondary}=navigationLayout(visibleNavItems,user?.role,compact);
  const currentSection=sectionForPath(visibleNavItems,location.pathname);
  const secondaryActive=secondary.some(item=>item===currentSection);

  // Co-sign inbox count — only providers see it; admin/billing roles
  // skip the fetch entirely (endpoint would return [] for them anyway,
  // but cheaper to not poll).
  const isProvider = user?.is_provider || user?.role === 'doctor' || user?.role === 'owner';
  const canShowCosigns = !!user?.id && isProvider && visibleNavItems.some((item) => item.to === '/clinical');
  const canShowTasks = !!user?.id && visibleNavItems.some((item) => item.to === '/front-desk-tasks') && tenantHasFeature(tenant, 'insurance_billing');
  const { data: cosignData } = useQuery({
    queryKey: ['cosign-inbox-count', user?.id, tenant?.id],
    queryFn: () => getCosignInbox().then((d) => d.notes?.length || 0),
    enabled: canShowCosigns,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  });
  const cosignCount = canShowCosigns && typeof cosignData === 'number' ? cosignData : 0;

  // Open front-desk task count — drives the Front Desk nav badge.
  // Poll only when this user can see the badge and the insurance task
  // feature is available. Other front-desk work stays accessible.
  const { data: frontDeskData } = useQuery({
    queryKey: ['claim-action-request-count', user?.id, tenant?.id],
    enabled: canShowTasks,
    queryFn: () => getOpenClaimActionRequestCount().then((r) => r.data?.total ?? r.data?.open ?? 0),
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  });
  const frontDeskCount = canShowTasks && typeof frontDeskData === 'number' ? frontDeskData : 0;

  // Unread patient-message threads needing a reply, scoped to the office the
  // user has selected (matches the inbox's location scope).
  const { data: msgData } = useQuery({
    queryKey: ['messages-unread-count', currentLocationId, user?.id, tenant?.id],
    enabled: !!user?.id,
    queryFn: () => getMessagesUnreadCount(currentLocationId).then((r) => r.data?.count || 0),
    refetchInterval: 45 * 1000,
    refetchOnWindowFocus: true,
    staleTime: 20 * 1000,
  });
  const messagesCount = typeof msgData === 'number' ? msgData : 0;

  // Native modal dialog provides background inertness and focus containment.
  // Its full-height layout does not assume a header or status-banner height.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const dialog = drawerRef.current;
    dialog?.showModal();
    const desktop = window.matchMedia('(min-width: 768px)');
    const onResize = () => { if (desktop.matches) setMobileOpen(false); };
    desktop.addEventListener('change', onResize);
    onResize();
    return () => {
      desktop.removeEventListener('change', onResize);
      if (dialog?.open) dialog.close();
      const opener = openerRef.current;
      const restore = opener?.getClientRects().length ? opener
        : opener?.closest('header')?.querySelector('nav a[aria-current="page"]') || opener?.closest('header')?.querySelector('nav a');
      if (restore?.isConnected && restore.getClientRects().length) restore.focus();
    };
  }, [mobileOpen]);
  useEffect(() => { setMobileOpen(false); if(moreRef.current)moreRef.current.open=false; }, [location.pathname, location.search, location.hash, user?.id, user?.role, tenant?.id]);

  function badgeFor(item) {
    return item.to === '/clinical' ? cosignCount : item.to === '/front-desk-tasks' ? frontDeskCount : item.to === '/messages' ? messagesCount : 0;
  }
  function badgeLabel(item, count) {
    return item.to === '/clinical' ? `${count} notes awaiting your co-sign` : item.to === '/front-desk-tasks' ? `${count} tasks waiting` : `${count} conversations need a reply`;
  }

  async function handleLogout() {
    try { await logout(refreshToken); } catch {}
    clearAuth();
    navigate('/login', { replace: true });
  }

  function renderLink(item,surface) {
    const count=badgeFor(item),Icon=item.icon;
    const light=surface==='more';
    return <NavLink key={item.to} to={item.to} title={item.to==='/clinical'?'Clinical Notes (SOAP notes)':item.label}
      aria-label={count>0?`${item.label} — ${badgeLabel(item,count)}`:item.label}
      className={({isActive})=>`flex items-center gap-2 rounded-lg ${surface==='primary'?'shrink-0 px-2 py-1.5 text-[13px]':'px-3 py-2.5 text-sm'} font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-400 ${light?(isActive?'bg-teal-50 text-teal-900':'text-gray-600 hover:bg-gray-50 hover:text-gray-900'):(isActive?'bg-white/15 text-white':'text-teal-100/80 hover:bg-white/10 hover:text-white')}`}>
      <Icon aria-hidden="true" className={surface==='primary'?'hidden h-4 w-4 shrink-0 xl:block':'h-4 w-4 shrink-0'}/><span className={surface==='primary'?'whitespace-nowrap':'flex-1'}>{item.label}</span>
      {count>0&&<span aria-label={badgeLabel(item,count)} className={`ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white ${item.to==='/clinical'?'bg-amber-600':'bg-rose-600'}`}>{count>9?'9+':count}</span>}
    </NavLink>;
  }

  return (
    <>
      <header className="relative z-30 shrink-0 bg-[#0f3d3a] shadow-sm">
       <div className="flex h-14 items-center gap-2 px-3 sm:px-4 md:h-12">
        {/* Mobile menu button */}
        <button ref={openerRef} aria-expanded={mobileOpen} aria-controls="mobile-navigation" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Close menu" : "Open menu"} title="Menu" className="mr-1 flex h-10 w-10 items-center justify-center rounded-md text-teal-200 hover:bg-white/10 md:hidden">
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        {/* Logo */}
        <NavLink to="/dashboard" aria-label="Cervica home" className="flex shrink-0 items-center gap-2 mr-1">
          {/* Logo kit v1: the four-vertebrae spine icon (from the wordmark's
              "i") — teal reads well on the dark bar. */}
          <img src="/brand/cervica/cervica-icon-transparent.svg" alt="" className="h-7 w-7" />
          <span className="hidden text-[15px] font-bold tracking-tight text-white xl:inline">Cervica</span>
        </NavLink>

        <span className="min-w-0 truncate text-sm font-semibold text-white md:hidden">{currentSection?.label || 'Cervica'}</span>

       <nav aria-label="Primary navigation" className="hidden min-w-0 items-center gap-1 md:flex">
        {primary.map(item=>renderLink(item,'primary'))}
        {secondary.length>0&&<details ref={moreRef} className="group shrink-0">
          <summary className={`flex cursor-pointer list-none items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-medium outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-300 [&::-webkit-details-marker]:hidden ${secondaryActive?'bg-white/15 text-white':'text-teal-100 hover:bg-white/10'}`}>
            More<ChevronDown aria-hidden="true" className="h-4 w-4 transition-transform group-open:rotate-180"/>
            {secondary.some(item=>badgeFor(item)>0)&&<span aria-label="More sections have pending work" className="h-2 w-2 rounded-full bg-amber-400"/>}
          </summary>
          <div className="absolute right-3 top-full z-50 mt-2 grid w-[min(38rem,calc(100vw-2rem))] grid-cols-2 gap-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-xl text-gray-900">
            {navigationGroups.map(group=>{const items=secondary.filter(item=>item.group===group);return items.length?<section key={group} aria-label={group}><h2 className="mb-2 px-2 text-[11px] font-bold uppercase tracking-wider text-gray-500">{group}</h2>{items.map(item=>renderLink(item,'more'))}</section>:null;})}
          </div>
        </details>}
        {secondaryActive&&<span className="sr-only" aria-label="Current section">{currentSection.label}</span>}
       </nav>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right side */}
        <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
          {/* Location selector */}
          {locations?.length > 0 && (
            <div className="hidden items-center gap-1.5 rounded-md bg-white/10 px-2 py-1 sm:flex">
              <button type="button" aria-label="Select location" title="Select location" className="flex items-center justify-center text-teal-300" onClick={(e) => e.currentTarget.nextElementSibling?.focus()}>
                <MapPin className="h-3 w-3" />
              </button>
              <select
                aria-label="Select location"
                title="Select location"
                value={currentLocationId || ''}
                onChange={(e) => setCurrentLocation(e.target.value || null)}
                className="max-w-[120px] xl:max-w-[160px] truncate appearance-none border-none bg-transparent text-[12px] font-medium text-teal-100 focus:outline-none cursor-pointer"
              >
                <option value="" className="text-gray-900">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id} className="text-gray-900">{loc.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notifications — hidden until feature is ready */}

          {/* User avatar + Logout */}
          <div className="flex items-center gap-1.5 ml-1 border-l border-white/10 pl-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-400/20 text-[10px] font-bold text-teal-300">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="hidden max-w-[130px] truncate 2xl:block">
              <p className="text-[11px] font-medium text-white leading-none">{user?.first_name} {user?.last_name}</p>
              <p className="text-[10px] text-teal-300/50 capitalize leading-none mt-0.5">{user?.role?.replace('_', ' ')}</p>
            </div>
            <button onClick={handleLogout} className="flex h-7 w-7 items-center justify-center rounded-md text-teal-300/40 transition-colors hover:bg-white/10 hover:text-white" title="Sign out">
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Office is already named in the selector. */}

        </div>
       </div>

      </header>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <dialog ref={drawerRef} id="mobile-navigation" aria-label="Main navigation" aria-modal="true"
          onCancel={(event) => { event.preventDefault(); setMobileOpen(false); }}
          onClick={(event) => {
            if (event.target !== event.currentTarget) return;
            const bounds = event.currentTarget.getBoundingClientRect();
            if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) setMobileOpen(false);
          }}
          className="fixed inset-y-0 left-0 right-auto m-0 h-dvh max-h-none w-72 max-w-[90vw] flex-col overflow-hidden border-0 bg-[#0f3d3a] p-0 text-white shadow-xl backdrop:bg-black/40 open:flex">
          <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-4 py-3">
            <span className="font-semibold">Cervica</span>
            <button type="button" autoFocus aria-label="Close menu" onClick={() => setMobileOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-md text-teal-100 hover:bg-white/10"><X className="h-5 w-5" /></button>
          </div>
            {/* Location selector (mobile) */}
            {locations?.length > 0 && (
              <div className="shrink-0 px-4 py-3 border-b border-white/10">
                <label className="flex items-center gap-1.5 cursor-pointer" title="Select location">
                  <MapPin className="h-3 w-3 text-teal-300" aria-hidden="true" />
                  <span className="sr-only">Select location</span>
                  <select
                    aria-label="Select location"
                    value={currentLocationId || ''}
                    onChange={(e) => setCurrentLocation(e.target.value || null)}
                    className="appearance-none border-none bg-transparent text-[13px] font-medium text-teal-100 focus:outline-none cursor-pointer"
                  >
                    <option value="" className="text-gray-900">All Locations</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id} className="text-gray-900">{loc.name}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            <nav aria-label="Main sections" className="min-h-0 flex-1 overflow-y-auto p-2">
              {navigationGroups.map(group=>{const items=visibleNavItems.filter(item=>item.group===group);return items.length?<section key={group} aria-label={group} className="mb-4"><h2 className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-widest text-teal-200/60">{group}</h2>{items.map(item=>renderLink(item,'mobile'))}</section>:null;})}
            </nav>

            {/* User info (mobile) */}
            <div className="shrink-0 border-t border-white/10 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-400/20 text-[12px] font-bold text-teal-300">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
                <div>
                  <p className="text-[13px] font-medium text-white">{user?.first_name} {user?.last_name}</p>
                  <p className="text-[11px] text-teal-300/50 capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
        </dialog>
      )}
    </>
  );
}
